import { useMemo } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SEOHead from "../../components/feature/SEOHead";

type Credential = { label: string; value: string };
type Value = { icon: string; title: string; desc: string };

export default function About() {
  const { t } = useTranslation();
  const credentials = t("aboutPage.credentials", { returnObjects: true }) as Credential[];
  const values = t("aboutPage.values", { returnObjects: true }) as Value[];

  const siteUrl = (import.meta.env.VITE_SITE_URL as string | undefined) || "https://willstrusts-junlawfirm.com";

  const jsonLd = useMemo(() => [
    {
      "@context": "https://schema.org",
      "@type": "Person",
      "name": "Hong-min Jun",
      "jobTitle": "Attorney at Law",
      "description": "Estate planning attorney licensed in Indiana and Illinois, specializing in wills, trusts, and prenuptial agreements for immigrant families.",
      "url": `${siteUrl}/about`,
      "telephone": "+13177012768",
      "email": "info@junlawoffice.com",
      "knowsLanguage": ["English", "Korean", "Chinese"],
      "alumniOf": [
        { "@type": "CollegeOrUniversity", "name": "Indiana University McKinney School of Law" },
        { "@type": "CollegeOrUniversity", "name": "Indiana University Maurer School of Law" }
      ],
      "hasCredential": [
        { "@type": "EducationalOccupationalCredential", "name": "J.D.", "credentialCategory": "degree" },
        { "@type": "EducationalOccupationalCredential", "name": "M.C.L.", "credentialCategory": "degree" }
      ],
      "worksFor": {
        "@type": "LegalService",
        "name": "Law Office of Hong-min Jun",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "10150 Lantern Rd, Suite #175",
          "addressLocality": "Fishers",
          "addressRegion": "IN",
          "postalCode": "46037",
          "addressCountry": "US"
        }
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "About Attorney Hong-min Jun | Multilingual Estate Planning Lawyer Fishers IN",
      "description": "Meet Hong-min Jun, an Indiana & Illinois licensed estate planning attorney serving immigrant families in English, Korean & Chinese.",
      "url": `${siteUrl}/about`,
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": siteUrl },
          { "@type": "ListItem", "position": 2, "name": "About", "item": `${siteUrl}/about` }
        ]
      }
    }
  ], [siteUrl]);

  return (
    <div className="min-h-screen flex flex-col bg-[#EDEEF3]">
      <SEOHead
        title="About Attorney Hong-min Jun | Multilingual Estate Planning Lawyer Fishers IN"
        description="Meet Hong-min Jun, an Indiana & Illinois licensed estate planning attorney serving immigrant families in English, Korean & Chinese. J.D. from IU McKinney School of Law."
        keywords="Hong-min Jun attorney, estate planning lawyer Indiana, multilingual attorney Fishers, Korean attorney Indiana"
        canonicalPath="/about"
        jsonLd={jsonLd}
      />
      <Navbar />
      <main className="flex-1">
        {/* Hero */}
        <section className="relative pt-40 pb-24 bg-[#EDEEF3] overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-20 -right-20 w-[500px] h-[500px] rounded-full border border-[#1E202A]/[0.03]" />
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-6">
            <span className="text-[#8A7A6A] block leading-none mb-2" style={{ fontFamily: "'Great Vibes', cursive", fontSize: "44px" }}>
              {t("aboutPage.heroCalligraphy")}
            </span>
            <h1
              className="text-[#1E202A] leading-tight max-w-2xl mb-6"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(38px, 5vw, 62px)", fontWeight: 300 }}
            >
              {t("aboutPage.heroH1Line1")}
              <br />
              <em>{t("aboutPage.heroH1Line2")}</em>
            </h1>
            <p className="text-[#74758A] text-sm leading-[2] max-w-lg">{t("aboutPage.heroDesc")}</p>
          </div>
        </section>

        {/* Attorney Profile Photo — standalone */}
        <section className="bg-[#E4E5EC] py-0 overflow-hidden">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 items-stretch">

              {/* Photo */}
              <div className="relative w-full h-[520px] lg:h-[640px] flex items-end justify-center overflow-hidden">
                <img
                  src="https://public.readdy.ai/ai/img_res/08bd4238-f1bb-4c6e-bd45-71047a0add71.png"
                  alt="Attorney Hong-min Jun"
                  className="h-full w-auto max-w-full object-contain object-bottom"
                  style={{ display: "block" }}
                />
              </div>

              {/* Caption */}
              <div className="flex flex-col justify-center px-10 lg:px-16 py-16">
                <span
                  className="text-[#8A7A6A] block leading-none mb-4"
                  style={{ fontFamily: "'Great Vibes', cursive", fontSize: "42px" }}
                >
                  {t("aboutPage.attyCalligraphy")}
                </span>
                <h2
                  className="text-[#1E202A] leading-tight mb-2"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(32px, 4vw, 52px)", fontWeight: 300 }}
                >
                  {t("brand.attorneyName")}
                </h2>
                <p
                  className="tracking-[0.2em] uppercase mb-3"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "11px", fontWeight: 600, color: "#B8942A" }}
                >
                  {t("aboutPage.attyTitle")}
                </p>
                <p className="text-[#74758A] text-xs tracking-widest mb-10">
                  {t("aboutPage.attyExp")}
                </p>

                <div className="w-10 h-px bg-[#8A7A6A]/40 mb-10" />

                <p
                  className="text-[#74758A] italic leading-relaxed"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(17px, 1.8vw, 21px)", fontWeight: 300 }}
                >
                  {t("aboutPage.attyQuote")}
                </p>
              </div>

            </div>
          </div>
        </section>

        {/* Attorney Profile — text & credentials */}
        <section className="py-24 bg-[#E4E5EC]">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
              <div className="lg:col-span-12 pt-2">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
                  <div>
                    <p className="text-[#74758A] text-sm leading-[2] mb-6">{t("aboutPage.attyBio1")}</p>
                    <p className="text-[#74758A] text-sm leading-[2]">{t("aboutPage.attyBio2")}</p>
                  </div>
                  <div className="flex flex-col gap-0">
                    {Array.isArray(credentials) && credentials.map((item, i, arr) => (
                      <div key={i} className={`flex items-center justify-between gap-6 py-4 ${i < arr.length - 1 ? "border-b border-[#D0D1DB]" : ""}`}>
                        <span className="text-[#A8AABB] text-[11px] tracking-widest uppercase shrink-0">{item.label}</span>
                        <span className="text-[#1E202A] text-right leading-snug" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "15px" }}>{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mission & Values — merged */}
        <section className="py-24 bg-[#EDEEF3]">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">

              {/* Left: intro */}
              <div>
                <span className="text-[#8A7A6A] block leading-none mb-2" style={{ fontFamily: "'Great Vibes', cursive", fontSize: "36px" }}>
                  {t("aboutPage.valuesCalligraphy")}
                </span>
                <h2
                  className="text-[#1E202A] leading-tight mb-8"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(26px, 3.5vw, 40px)", fontWeight: 300 }}
                >
                  {t("aboutPage.valuesTitle1")}
                  <br />
                  <em>{t("aboutPage.valuesTitle2")}</em>
                </h2>
                <p className="text-[#74758A] text-sm leading-[2] mb-5">{t("aboutPage.storyP1")}</p>
                <p className="text-[#74758A] text-sm leading-[2]">{t("aboutPage.storyP2")}</p>
              </div>

              {/* Right: value cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-0">
                {Array.isArray(values) && values.map((v, i) => (
                  <div
                    key={i}
                    className={`flex items-start gap-5 py-8 px-2
                      ${i % 2 === 0 ? "sm:pr-10" : "sm:pl-10 sm:border-l border-[#D0D1DB]"}
                      ${i < 2 ? "border-b border-[#D0D1DB]" : ""}
                    `}
                  >
                    <div className="w-8 h-8 flex items-center justify-center shrink-0 mt-1">
                      <i className={`${v.icon} text-[#8A7A6A] text-lg`} />
                    </div>
                    <div>
                      <h3 className="text-[#1E202A] mb-1" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "19px", fontWeight: 500 }}>{v.title}</h3>
                      <p className="text-[#74758A] text-xs leading-[1.9]">{v.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-28 bg-[#D9DAE5]">
          <div className="max-w-3xl mx-auto px-6 text-center relative">
            <span className="absolute inset-0 flex items-center justify-center text-[#1E202A]/[0.03] select-none pointer-events-none leading-none" style={{ fontFamily: "'Great Vibes', cursive", fontSize: "200px" }}>
              Legacy
            </span>
            <div className="relative z-10">
              <span className="text-[#8A7A6A] block leading-none mb-2" style={{ fontFamily: "'Great Vibes', cursive", fontSize: "40px" }}>
                {t("aboutPage.ctaCalligraphy")}
              </span>
              <h2 className="text-[#1E202A] mb-5" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(26px, 3.5vw, 40px)", fontWeight: 300 }}>
                {t("aboutPage.ctaTitle1")} <em>{t("aboutPage.ctaTitle2")}</em>
              </h2>
              <p className="text-[#74758A] text-sm leading-[2] mb-8 max-w-md mx-auto">{t("aboutPage.ctaDesc")}</p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-[#1E202A] text-[#EDEEF3] px-10 py-4 rounded-full text-sm font-medium hover:bg-[#8A7A6A] transition-all duration-300 cursor-pointer whitespace-nowrap"
                style={{ letterSpacing: "0.02em" }}
              >
                {t("aboutPage.ctaBtn")}
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
